import {
  Avatar,
  Css,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  PageHeaderLayout,
  RowStyles,
  SimpleHeaderAndData,
  column,
  dateColumn,
  selectColumn,
  simpleDataRows,
  simpleHeader,
  treeFilter,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import {
  createProductOfferingConfigEditUrl,
  createProductOfferingConfigsCompareUrl,
  createProductOfferingUrl,
  createProductOfferingsUrl,
} from "src/RouteUrls";
import { dateCell } from "src/components";
import { Order, ProductOfferingConfigOrder, ProductOfferingConfigsFilter } from "src/generated/graphqlTypes";
import {
  MarketProductOfferingConfigsPageMetadataFragment,
  ProductOfferingConfigDetailFragment,
  useProductOfferingConfigsPageMetadataQuery,
  useProductOfferingConfigsQuery,
} from "src/routes/libraries/product-offering-configs/ProductOfferingConfigsPage.generated";
import { queryResult } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";

const productOfferingConfigsPageSize = 100;

export function ProductOfferingConfigsPage() {
  const query = useProductOfferingConfigsPageMetadataQuery();
  return queryResult(query, { data: ({ markets }) => <ProductOfferingConfigsPageView marketsOrIds={markets} /> });
}

export type ProductOfferingConfigsPageViewProps = {
  // Configures the view to show all markets and developments, a specific development, a specific project, or a specific product offering.
  marketsOrIds:
    | MarketProductOfferingConfigsPageMetadataFragment[]
    | { developmentId?: string; projectId?: string; productOfferingId?: string; rpavId?: string };
};

export function ProductOfferingConfigsPageView(props: ProductOfferingConfigsPageViewProps) {
  const { marketsOrIds } = props;
  const isGlobal = Array.isArray(marketsOrIds);
  const {
    developmentId,
    projectId,
    productOfferingId,
    rpavId: rpavIdFromProps,
  } = isGlobal
    ? { developmentId: undefined, projectId: undefined, productOfferingId: undefined, rpavId: undefined }
    : marketsOrIds;
  const [order, setOrder] = useState<ProductOfferingConfigOrder>({ updatedAt: Order.Desc });
  const filterDefs: FilterDefs<ProductOfferingConfigsFilter> = useMemo(
    () =>
      isGlobal
        ? {
            developments: treeFilter({
              defaultCollapsed: true,
              label: "Markets and Developments",
              filterBy: "leaf",
              getOptionLabel: (o) => o.name,
              getOptionValue: (o) => o.id,
              options: marketsOrIds.map((m) => ({
                id: m.id,
                name: m.name,
                children: m.developments.map((d) => ({ id: d.id, name: d.name })),
              })),
            }),
          }
        : {},
    [isGlobal, marketsOrIds],
  );
  const layoutState = useGridTableLayoutState<ProductOfferingConfigsFilter>({
    persistedFilter: {
      // Scoped lists have no filter fields. A separate key keeps a saved global market filter from applying here.
      storageKey: isGlobal ? "productOfferingConfigsFilter" : "scopedProductOfferingConfigsFilter",
      filterDefs,
    },
    search: "server",
  });
  const query = useProductOfferingConfigsQuery({
    variables: {
      filter: {
        search: layoutState.searchString || undefined,
        ...layoutState.filter,
        // Scope ids are authoritative, so a persisted market filter cannot widen a development or offering list.
        ...(projectId ? { projects: [projectId] } : {}),
        ...(developmentId ? { developments: [developmentId] } : {}),
        ...(productOfferingId ? { productOfferings: [productOfferingId] } : {}),
      },
      order,
      page: { offset: 0, limit: productOfferingConfigsPageSize },
    },
  });
  const maybeFetchMore = useCallback(async () => {
    const configs = query.data?.productOfferingConfigs;
    if (!configs || configs.entities.length >= configs.pageInfo.totalCount) return;
    await query.fetchMore({
      variables: { page: { offset: configs.entities.length, limit: productOfferingConfigsPageSize } },
    });
  }, [query]);
  const configs = query.data?.productOfferingConfigs.entities;
  const rpavId = rpavIdFromProps ?? rpavIdFromConfig(configs?.[0]);
  const backUrl = productOfferingId
    ? rpavId
      ? createProductOfferingUrl(productOfferingId, rpavId, developmentId, projectId)
      : createProductOfferingsUrl(developmentId, projectId)
    : createProductOfferingsUrl(developmentId, projectId);
  const description = productOfferingId
    ? "Compare the configurations created on the offering"
    : "Select as many saved configurations on product offerings below, then select compare";
  const api = useGridTableApi<Row>();
  const selectedRows = useComputed(() => api.getSelectedRows(), [api]);
  const hideDevelopment = !!(developmentId || projectId);
  const columns = useMemo(() => createColumns(hideDevelopment), [hideDevelopment]);
  const rowStyles: RowStyles<Row> = useMemo(
    () => ({
      data: {
        rowLink: ({ id, data: { productOffering } }) =>
          createProductOfferingConfigEditUrl({
            productOfferingConfigId: id,
            // Update url params with metaIds to load the page with selectable options & delta costs
            productOfferingId: productOffering.id,
            developmentId,
            projectId,
          }),
      },
    }),
    [developmentId, projectId],
  );
  const disableCompare = useMemo(
    () =>
      (selectedRows.isEmpty && "No configurations selected") ||
      (selectedRows.length > 10 && "No greater than 10 configurations can be compared at once"),
    [selectedRows.isEmpty, selectedRows.length],
  );
  const isEmptyOffering = !query.loading && !!productOfferingId && configs?.isEmpty;

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Compare Configurations",
        breadcrumbs: {
          breadcrumbs: [{ href: backUrl, label: productOfferingId ? "Product Offering" : "Product Offerings" }],
        },
        actions: [
          {
            label: "Compare",
            variant: "primary",
            disabled: disableCompare,
            onClick: createProductOfferingConfigsCompareUrl(
              selectedRows.map((r) => r.id),
              productOfferingId,
              developmentId,
              projectId,
            ),
          },
        ],
      }}
    >
      <p css={Css.pxPx(24).sm.layoutContainer.$}>{description}</p>
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{
          api,
          columns,
          rowStyles,
          query,
          createRows: (tableData) => [simpleHeader, ...simpleDataRows(tableData?.productOfferingConfigs.entities)],
          sorting: {
            on: "server",
            onSort: (key, direction) => setOrder(toOrder(key, direction)),
            value: parseOrder(order),
          },
          as: "virtual",
          emptyFallback: "There aren't any configurations to display.",
          infiniteScroll: { onEndReached: maybeFetchMore },
        }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<ProductOfferingConfigDetailFragment>;

function rpavIdFromConfig(config?: ProductOfferingConfigDetailFragment): string | undefined {
  const templates = config?.productOffering?.itemTemplates;
  return templates?.find((t) => t.isLatestActive)?.id ?? templates?.[0]?.id;
}

const createColumns = (hideDevelopment: boolean): GridColumn<Row>[] => [
  selectColumn(),
  column<Row>({ header: "Name", data: ({ name }) => name, mw: "200px" }),
  column<Row>({ header: "Offering", data: ({ productOffering }) => productOffering.name, mw: "200px" }),
  column<Row>({
    header: "Created by",
    data: ({ createdBy }) => (
      <Avatar src={createdBy?.internalUser?.avatar} name={createdBy.internalUser?.name} showName />
    ),
    mw: "180px",
  }),
  dateColumn<Row>({ header: "Created", data: ({ createdAt }) => dateCell(createdAt), mw: "120px" }),
  dateColumn<Row>({
    header: "Updated",
    serverSideSortKey: "updatedAt",
    data: ({ updatedAt }) => dateCell(updatedAt),
    mw: "120px",
  }),
  ...(hideDevelopment
    ? []
    : [
        column<Row>({
          header: "Developments",
          data: ({ productOffering }) => productOffering.development?.name,
          mw: "180px",
        }),
      ]),
];
